import { useState } from "react";
import { Link } from "react-router-dom";
import {
  IconApi,
  IconArrowsSort,
  IconBolt,
  IconBriefcase,
  IconBuilding,
  IconBuildingStore,
  IconChartDonut4,
  IconChartDots3,
  IconChevronDown,
  IconClipboardCheck,
  IconCode,
  IconColorSwatch,
  IconFilter,
  IconLink,
  IconPalette,
  IconPlugConnected,
  IconRepeat,
  IconRocket,
  IconSend,
  IconShieldLock,
  IconStack2,
  IconTargetArrow,
  IconTruck,
  IconUsers,
  IconUsersGroup,
  IconWorld,
} from "@tabler/icons-react";
import { Logo } from "../../constants";
import styles from "./APIPage.module.css";
import { submitPartnerAssessmentForm } from "../../services/talentCalls";
import { checkMail } from "../../utils/functions";
import { errorHandler, toastSuccess } from "../../utils";
import RoiCalculator from "./RoiCalculator";
import { ADS_CONVERSIONS, trackAdsConversion } from "../../utils/adsConversions";
import { getFormUtm } from "../../utils/attribution";
import { openCookieSettings } from "../../utils/consent";

const TRUST_LOGOS = [
  { icon: IconUsersGroup, label: "Recruitment agencies" },
  { icon: IconBuilding, label: "HR SaaS platforms" },
  { icon: IconBriefcase, label: "Staffing firms" },
  { icon: IconWorld, label: "Talent marketplaces" },
  { icon: IconRepeat, label: "Assessment resellers" },
];

const FEATURES = [
  {
    icon: IconChartDonut4,
    title: "Candidate-fit scoring",
    body: "Rank a whole candidate pool against your job spec by an explainable fit % — the strongest candidates surface first, far beyond CV keyword matching.",
  },
  {
    icon: IconApi,
    title: "One API call to assess",
    body: "Send a candidate's name, email and role. We host the test, score it, and return the result.",
  },
  {
    icon: IconChartDots3,
    title: "Psychometric & role-calibrated",
    body: "Scenario-based questions, AI-scored against the traits that matter for each specific role.",
  },
  {
    icon: IconStack2,
    title: "Built for volume",
    body: "Screen hundreds of applicants per role across every hire — reliable, rate-limited, priced for scale.",
  },
  {
    icon: IconBuildingStore,
    title: "Keep your margin",
    body: "Offer assessment as part of your service, priced how you like. We just power it underneath.",
  },
  {
    icon: IconPlugConnected,
    title: "Fast to integrate",
    body: "REST API, signed webhooks and a developer portal. Weeks, not quarters, to go live.",
  },
];

const SESSIONS = [
  { candidate: "Candidate 01", role: "Business Analyst", status: "completed", result: "View →" },
  { candidate: "Candidate 02", role: "Data Scientist", status: "completed", result: "View →" },
  { candidate: "Candidate 03", role: "Recruiter", status: "in progress", result: "—" },
  { candidate: "Candidate 04", role: "Backend Developer", status: "pending", result: "—" },
];

const POOL_CANDIDATES = [
  { rank: 1, initials: "AO", avatarClass: "cfAv1", name: "Candidate 01", meta: "7 yrs · BSc Economics", pct: 92, fill: "high", top: true },
  { rank: 2, initials: "MK", avatarClass: "cfAv2", name: "Candidate 02", meta: "5 yrs · MBA", pct: 86, fill: "high" },
  { rank: 3, initials: "TA", avatarClass: "cfAv3", name: "Candidate 03", meta: "4 yrs · BSc Stats", pct: 74, fill: "mid" },
  { rank: 4, initials: "JI", avatarClass: "cfAv4", name: "Candidate 04", meta: "3 yrs · BA Business", pct: 61, fill: "low" },
];

const FIT_BREAKDOWN = [
  // 0.55 × 94 + 0.45 × 90 ≈ 92 — matches Candidate 01's fit %
  { label: "Skills & qualifications", pct: 94 },
  { label: "Relevant experience", pct: 90 },
];

const ROLE_CATEGORIES = [
  {
    icon: IconBriefcase,
    name: "Business & Finance",
    count: "17 roles",
    showLabel: "Show all 17 roles",
    groups: [
      {
        label: "Business Management",
        roles: [
          { name: "Business Analyst" },
          { name: "Project Manager" },
          { name: "Operations Manager" },
          { name: "Business Development Manager" },
          { name: "Product Manager" },
        ],
      },
      {
        label: "Finance",
        more: true,
        roles: [
          { name: "Audit Trainee" },
          { name: "Accountant" },
          { name: "Financial Analyst" },
          { name: "Finance Manager" },
          { name: "Wealth Advisor" },
          { name: "Portfolio Manager" },
        ],
      },
      {
        label: "HR & Administration",
        more: true,
        roles: [
          { name: "Administration Assistant" },
          { name: "Personal Assistant" },
          { name: "Administrative Secretary" },
          { name: "Executive Assistant" },
          { name: "Recruiter" },
          { name: "HR Officer / HR Manager" },
        ],
      },
    ],
  },
  {
    icon: IconCode,
    name: "Technology & Digital",
    count: "18 roles",
    showLabel: "Show all 18 roles",
    groups: [
      {
        label: "Software Engineering",
        roles: [
          { name: "Frontend Developer" },
          { name: "Backend Developer" },
          { name: "Mobile Developer" },
          { name: "Software Engineer" },
          { name: "Full Stack Developer" },
        ],
      },
      {
        label: "AI & Data",
        more: true,
        roles: [
          { name: "Data Analyst" },
          { name: "Data Scientist" },
          { name: "Data Engineer" },
          { name: "AI Engineer" },
          { name: "Machine Learning Engineer" },
        ],
      },
      {
        label: "Cloud & Infrastructure",
        more: true,
        roles: [
          { name: "Networking Hardware Specialist" },
          { name: "DevOps Engineer" },
          { name: "Cloud Engineer" },
          { name: "Site Reliability Engineer" },
        ],
      },
      {
        label: "Security",
        more: true,
        roles: [
          { name: "Information Security Specialist" },
          { name: "Cybersecurity Specialist" },
        ],
      },
      {
        label: "Quality Assurance",
        more: true,
        roles: [
          { name: "QA Engineer" },
          { name: "Lead QA Engineer" },
          { name: "Test Automation Engineer" },
        ],
      },
      {
        label: "IT Support",
        more: true,
        roles: [
          { name: "IT Specialist" },
          { name: "Technical Support Engineer" },
          { name: "Systems Administrator" },
        ],
      },
    ],
  },
  {
    icon: IconPalette,
    name: "Creative & Design",
    count: "7 roles",
    showLabel: "Show all 7 roles",
    groups: [
      {
        label: "Product & UX",
        roles: [
          { name: "UI/UX Designer" },
          { name: "Product Designer" },
        ],
      },
      {
        label: "Marketing",
        roles: [
          { name: "Digital Marketer" },
          { name: "Marketing Manager" },
        ],
      },
      {
        label: null,
        more: true,
        roles: [{ name: "Marketing & Communications Manager" }],
      },
      {
        label: "Architecture",
        more: true,
        roles: [{ name: "Architect" }],
      },
      {
        label: "Creative",
        more: true,
        roles: [{ name: "Writer" }],
      },
    ],
  },
  {
    icon: IconTruck,
    name: "Sales & Operations",
    count: "16 roles",
    showLabel: "Show all 16 roles",
    groups: [
      {
        label: "Sales",
        roles: [
          { name: "Sales Representative" },
          { name: "Sales Associate (Banking)" },
          { name: "Sales Engineer" },
          { name: "Real Estate Sales Representative" },
        ],
      },
      {
        label: "Customer Support",
        more: true,
        roles: [
          { name: "Customer Service Representative" },
          { name: "Call Centre Agent" },
          { name: "Call Centre Supervisor" },
          { name: "Call Centre Manager" },
        ],
      },
      {
        label: "Banking Operations",
        more: true,
        roles: [
          { name: "Bank Teller" },
          { name: "Fund Transfer Officer" },
          { name: "Banking Customer Service" },
        ],
      },
      {
        label: "Logistics & Field Operations",
        more: true,
        roles: [
          { name: "Dispatch Rider" },
          { name: "Site Acquisition Agent" },
          { name: "Facility Manager" },
        ],
      },
      {
        label: "Industrial & Retail",
        more: true,
        roles: [
          { name: "Mechanical Technician" },
          { name: "Workshop Lead Technician" },
          { name: "Supermarket Manager" },
        ],
      },
    ],
  },
];

const WHY = [
  {
    icon: IconColorSwatch,
    quote:
      "Ship in weeks, not a year. Add psychometric testing to your product instead of building an assessment engine from scratch.",
    avatar: IconRocket,
    avatarClass: "av1",
    name: "Fast to launch",
    role: "Live in weeks, not months",
    feat: true,
  },
  {
    icon: IconLink,
    quote:
      "One shared link per role means candidates test themselves — post it anywhere and results flow straight back to your dashboard, no manual sending.",
    avatar: IconUsers,
    avatarClass: "av3",
    name: "Self-serve at scale",
    role: "Shared links & CSV bulk",
  },
  {
    icon: IconPlugConnected,
    quote:
      "A clean REST API, signed webhooks and a developer portal — so results arrive in real time and slot straight into the workflow you already run.",
    avatar: IconCode,
    avatarClass: "av4",
    name: "Developer-friendly",
    role: "REST · webhooks · portal",
  },
  {
    icon: IconChartDonut4,
    quote:
      "Don't just screen — rank. Score a whole candidate pool by an explainable fit % that blends CV & skills with relevant experience, so the strongest candidates surface first.",
    avatar: IconTargetArrow,
    avatarClass: "av5",
    name: "Candidate-fit scoring",
    role: "Explainable · CV + experience",
  },
];

const RoleCard = ({ category, isOpen, onToggle }) => {
  const Icon = category.icon;

  return (
    <div className={`${styles.rcard} ${isOpen ? styles.rcardOpen : ""}`}>
      <div className={styles.rcardHead}>
        <span className={styles.ri}>
          <Icon size={19} />
        </span>
        <h3>{category.name}</h3>
        <span className={styles.cnt}>{category.count}</span>
      </div>
      {category.groups.map((group, gi) => {
        const isMore = Boolean(group.more);
        return (
          <div key={gi} className={isMore ? styles.rmore : undefined}>
            {group.label && <div className={styles.rsub}>{group.label}</div>}
            {group.roles.map((role) => (
              <div key={role.name} className={styles.rrow}>
                <span className={styles.rn}>{role.name}</span>
              </div>
            ))}
          </div>
        );
      })}
      <button
        type="button"
        className={`${styles.rshow} ${isOpen ? styles.rshowOpen : ""}`}
        onClick={onToggle}
      >
        <span>{isOpen ? "Show less" : category.showLabel}</span>
        <IconChevronDown size={15} />
      </button>
    </div>
  );
};

const APIPage = () => {
  const [openCards, setOpenCards] = useState(() => ROLE_CATEGORIES.map(() => false));

  const toggleCard = (index) => {
    setOpenCards((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  const [contactForm, setContactForm] = useState({
    company: "",
    name: "",
    email: "",
    volume: "",
    message: "",
    consent: false,
  });
  const [submittingContact, setSubmittingContact] = useState(false);

  const updateContactField = (field) => (e) =>
    setContactForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.company.trim()) {
      errorHandler("Company is required.", "Error");
      return;
    }
    if (!contactForm.name.trim()) {
      errorHandler("Your name is required.", "Error");
      return;
    }
    if (!checkMail(contactForm.email)) {
      errorHandler("Enter a valid work email.", "Error");
      return;
    }

    const parsedVolume = parseInt(contactForm.volume, 10);

    setSubmittingContact(true);
    try {
      await submitPartnerAssessmentForm({
        source: "partnerships_enquiry",
        companyName: contactForm.company,
        fullName: contactForm.name,
        email: contactForm.email,
        assesmentPerMonth: Number.isNaN(parsedVolume) ? undefined : parsedVolume,
        marketingConsent: contactForm.consent,
        ...getFormUtm(),
        additionalComments: contactForm.message,
      });
      trackAdsConversion(ADS_CONVERSIONS.PARTNERSHIP_ENQUIRY);
      toastSuccess("Thanks — we'll be in touch within two business days.");
      setContactForm({ company: "", name: "", email: "", volume: "", message: "", consent: false });
    } catch (error) {
      error?.response?.data?.errors
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setSubmittingContact(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* NAV */}
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <Link to="/" className={styles.logo}>
            <Logo color="#030659" />
            <span className={styles.apiBadge}>API</span>
          </Link>
          <ul className={styles.navLinks}>
            <li>
              <a href="#time-saved">Time saved</a>
            </li>
            <li>
              <a href="#benefits">Benefits</a>
            </li>
            <li>
              <a href="#sessions">Test sessions</a>
            </li>
            <li>
              <a href="#candidate-fit">Candidate fit</a>
            </li>
            <li>
              <a href="#why">Why partners</a>
            </li>
          </ul>
          <div className={styles.navCta}>
            <Link to="/partner-login" className={styles.navLogin}>
              <span className={styles.navLoginFull}>Partner login</span>
              <span className={styles.navLoginShort}>Log in</span>
            </Link>
            <a href="#contact" className={`${styles.btn} ${styles.btnPrimary}`}>
              <span className={styles.navCtaFull}>Talk to partnerships</span>
              <span className={styles.navCtaShort}>Let's talk</span>
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroGrid}>
            <div>
              <div className={styles.heroEyebrow}>
                <span className="dot" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--blue)" }} />
                Psychometric assessment API
              </div>
              <h1>
                Add assessment to your platform. <span className={styles.highlight}>Not the engineering.</span>
              </h1>
              <p className={styles.heroSub}>
                Give your clients role-specific psychometric testing over one simple API. Your candidates, our
                engine underneath.
              </p>
              <div className={styles.heroCta}>
                <a href="#contact" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}>
                  Talk to partnerships →
                </a>
                <a href="#candidate-fit" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnLg}`}>
                  See candidate fit
                </a>
              </div>
              <Link to="/partners" style={{ display: "inline-block", marginTop: 14, fontWeight: 700, color: "var(--blue)", textDecoration: "none" }}>
                Download the partner brochure →
              </Link>
              <div className={styles.heroMeta}>
                <div className={styles.heroMetaItem}>
                  <span className="check">✓</span> 58 roles covered
                </div>
                <div className={styles.heroMetaItem}>
                  <span className="check">✓</span> Live in weeks
                </div>
              </div>
              <a href="#time-saved" className={styles.roiJump}>
                Calculate the time you'd save →
              </a>
            </div>

            <div className={styles.heroVisual}>
              <div className={`${styles.floatCard} ${styles.fc1}`}>
                <div className={`${styles.floatIcon} ${styles.floatIconSuccess}`}>
                  <IconBolt size={18} />
                </div>
                <div className={styles.floatText}>
                  <strong>1 API call</strong>to assess a candidate
                </div>
              </div>
              <div className={styles.inviteCard}>
                <div className={styles.invTop}>
                  <div className={styles.invBrand}>
                    <span className={styles.invLogo}>
                      <IconColorSwatch size={16} />
                    </span>
                    Awasource
                  </div>
                  <span className={styles.invPill}>Assessment invite</span>
                </div>
                <div className={styles.invBody}>
                  <div className={styles.invBadge}>
                    <IconClipboardCheck size={26} />
                  </div>
                  <h3>You're invited to a quick assessment</h3>
                  <p>Business Analyst · about 20 minutes · no prep needed</p>
                  <div className={styles.invSteps}>
                    <div className={styles.invStep}>
                      <span>1</span> Answer a few real-world scenarios
                    </div>
                    <div className={styles.invStep}>
                      <span>2</span> We score your fit for the role with AI
                    </div>
                    <div className={styles.invStep}>
                      <span>3</span> Results go straight to the hiring team
                    </div>
                  </div>
                  <button className={styles.invBtn} type="button">
                    Start assessment →
                  </button>
                  <div className={styles.invFoot}>Real-time candidate assessment, powered by Awasource</div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.trustBar}>
            <div className={styles.trustLabel}>Built for the teams that assess candidates at scale</div>
            <div className={styles.trustLogos}>
              {TRUST_LOGOS.map(({ icon: Icon, label }) => (
                <div key={label} className={styles.trustLogo}>
                  <Icon size={16} /> {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TIME SAVED */}
      <section className={`${styles.section} ${styles.sectionSoft}`} id="time-saved">
        <div className={styles.container}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionEyebrow}>Time saved</span>
            <h2>Applicants × hours screening = time you get back.</h2>
            <p className={styles.sectionSub}>
              Set your volume, share the result, or email it to yourself.
            </p>
          </div>
          <RoiCalculator />
        </div>
      </section>

      {/* BENEFITS */}
      <section className={`${styles.section} ${styles.sectionSoft}`} id="benefits">
        <div className={styles.container}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionEyebrow}>Partner API</span>
            <h2>Everything you need to assess and rank candidates as your own.</h2>
            <p className={styles.sectionSub}>
              Skip months of building — plug in the API to assess candidates and score their fit, all under your
              own product.
            </p>
          </div>
          <div className={styles.featuresGrid}>
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon size={22} />
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEST SESSIONS */}
      <section className={styles.section} id="sessions">
        <div className={styles.container}>
          <div className={styles.solutionGrid}>
            <div style={{ alignSelf: "start" }}>
              <div className={styles.sessionsLabel}>Your sessions dashboard</div>
              <div className={styles.tableScroll}>
                <table className={styles.stbl}>
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SESSIONS.map((s) => (
                      <tr key={s.candidate}>
                        <td style={{ color: "var(--navy)", fontWeight: 600 }}>{s.candidate}</td>
                        <td>{s.role}</td>
                        <td>
                          <span
                            className={`${styles.spill} ${
                              s.status === "completed"
                                ? styles.spillDone
                                : s.status === "in progress"
                                  ? styles.spillProg
                                  : styles.spillPending
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td>{s.result === "—" ? "—" : <span style={{ color: "var(--blue)", fontWeight: 700 }}>{s.result}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className={styles.solutionText}>
              <span className={styles.sectionEyebrow}>Test sessions</span>
              <h2>Assign at any scale — and never chase a result.</h2>
              <p>
                Send one candidate or ten thousand, however suits your workflow. Everything lands back in one
                place — results in your dashboard and by webhook.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CANDIDATE-FIT SCORING */}
      <section className={styles.section} id="candidate-fit">
        <div className={styles.container}>
          <div className={styles.cfGrid}>
            <div className={styles.solutionText}>
              <span className={styles.cfEyebrow}>
                <span className={styles.cfDot} /> New · Candidate-Fit Scoring
              </span>
              <h2>Score your whole pool against your job spec — in one dashboard.</h2>
              <p>
                Upload your candidates' CVs (PDF or Word), point them at your job spec, and get every
                candidate ranked by an explainable fit %. One dashboard to view, sort and evaluate — no stack of
                PDFs to read by hand. Direct ATS import is coming soon.
              </p>
            </div>
            <div className={styles.cfPool}>
              <div className={styles.cfPoolTop}>
                <span className={`${styles.dotR} ${styles.dotRed}`} />
                <span className={`${styles.dotR} ${styles.dotYellow}`} />
                <span className={`${styles.dotR} ${styles.dotGreen}`} />
                <span className={styles.urlBar}>app.yourbrand.com/candidates</span>
              </div>
              <div className={styles.cfPoolBody}>
                <div className={styles.cfPoolHead}>
                  <h4>Candidate pool</h4>
                  <span className={styles.cfRolePill}>Business Analyst — Lagos</span>
                </div>
                <div className={styles.cfPoolSub}>
                  <IconUsers size={14} /> 128 applicants · vs your job spec
                </div>
                <div className={styles.cfSortbar}>
                  <span className={`${styles.cfChip} ${styles.cfChipActive}`}>
                    <IconArrowsSort size={13} /> Fit %
                  </span>
                  <span className={styles.cfChip}>Experience</span>
                  <span className={styles.cfChip}>CV & skills</span>
                  <span className={styles.cfChip}>
                    <IconFilter size={13} /> Filter
                  </span>
                </div>
                {POOL_CANDIDATES.map((c) => (
                  <div key={c.rank} className={`${styles.cfRow} ${c.top ? styles.cfRowTop : ""}`}>
                    <div className={styles.cfRank}>{c.rank}</div>
                    <div className={`${styles.cfAv} ${styles[c.avatarClass]}`}>{c.initials}</div>
                    <div className={styles.cfNm}>
                      <b>{c.name}</b>
                      <span>{c.meta}</span>
                    </div>
                    <div className={styles.cfTrack}>
                      <div
                        className={`${styles.cfFill} ${c.fill === "mid" ? styles.cfFillMid : c.fill === "low" ? styles.cfFillLow : ""}`}
                        style={{ width: `${c.pct}%` }}
                      />
                    </div>
                    <div className={`${styles.cfPct} ${c.pct >= 80 ? styles.cfPctGood : ""}`}>{c.pct}%</div>
                  </div>
                ))}
                <div className={styles.cfBreak}>
                  <div className={styles.cfBreakH}>
                    <IconChartDonut4 size={14} /> Why Candidate 01 scores 92%{" "}
                    <span className={styles.cfWhy}>Explainable</span>
                  </div>
                  {FIT_BREAKDOWN.map((seg) => (
                    <div key={seg.label} className={styles.cfSeg}>
                      <span className={styles.cfSegLabel}>{seg.label}</span>
                      <span className={styles.cfSegTrack}>
                        <span className={styles.cfSegFill} style={{ width: `${seg.pct}%` }} />
                      </span>
                      <span className={styles.cfSegVal}>{seg.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className={styles.stats}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            <div>
              <div className={styles.statNum}>58</div>
              <div className={styles.statLabel}>Roles covered</div>
            </div>
            <div>
              <div className={styles.statNum}>
                1<span className={styles.statAccent}> call</span>
              </div>
              <div className={styles.statLabel}>API to assess a candidate</div>
            </div>
            <div>
              <div className={styles.statNum}>
                Weeks<span className={styles.statAccent}>.</span>
              </div>
              <div className={styles.statLabel}>Not quarters, to integrate</div>
            </div>
            <div>
              <div className={styles.statNum}>
                Real-time<span className={styles.statAccent}>.</span>
              </div>
              <div className={styles.statLabel}>Results by webhook</div>
            </div>
          </div>
        </div>
      </section>

      {/* ROLE COVERAGE */}
      <section className={`${styles.section} ${styles.sectionSoft}`}>
        <div className={styles.container}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionEyebrow}>Role coverage</span>
            <h2>Every role we assess.</h2>
            <p className={styles.sectionSub}>
              58 role-calibrated tests across business, technology, creative and sales &amp; operations.
            </p>
          </div>
          <div className={styles.rolecats}>
            {ROLE_CATEGORIES.map((category, i) => (
              <RoleCard
                key={category.name}
                category={category}
                isOpen={openCards[i]}
                onToggle={() => toggleCard(i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* WHY PARTNERS */}
      <section className={styles.section} id="why">
        <div className={styles.container}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionEyebrow}>Why partners choose the API</span>
            <h2>Built to be resold, not just integrated.</h2>
            <p className={styles.sectionSub}>
              The difference between bolting on a testing tool and offering assessment as your own product.
            </p>
          </div>
          <div className={styles.testimonialGrid}>
            {WHY.map((item) => {
              const StarIcon = item.icon;
              const AvatarIcon = item.avatar;
              return (
                <div
                  key={item.name}
                  className={`${styles.testimonialCard} ${item.feat ? styles.testimonialFeat : ""}`}
                >
                  <div className={styles.stars}>
                    <StarIcon size={18} />
                  </div>
                  <p className={styles.testimonialQuote}>{item.quote}</p>
                  <div className={styles.testimonialAuthor}>
                    <div className={`${styles.avatar} ${styles[item.avatarClass]}`}>
                      <AvatarIcon size={18} />
                    </div>
                    <div className={styles.authorInfo}>
                      <div className={styles.authorName}>{item.name}</div>
                      <div className={styles.authorRole}>{item.role}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className={`${styles.section} ${styles.contactBand}`} id="contact">
        <div className={styles.container}>
          <div className={styles.cbandGrid}>
            <div className={styles.cbandText}>
              <span className={styles.sectionEyebrow}>Let's talk</span>
              <h2>Talk to partnerships.</h2>
              <p>
                Tell us your volume and we'll come back with a tailored plan and next steps — usually within two
                business days. Here's everything the API gives you:
              </p>
              <div className={styles.apifeat}>
                <div className={styles.apifeatGroup}>
                  <div className={styles.apifeatH}>
                    <IconClipboardCheck size={15} /> Assessment engine
                  </div>
                  <ul>
                    <li>58 role-calibrated psychometric tests</li>
                    <li>Scenario-based, AI-scored to each role</li>
                  </ul>
                </div>
                <div className={styles.apifeatGroup}>
                  <div className={styles.apifeatH}>
                    <IconChartDonut4 size={15} /> Candidate-fit scoring
                  </div>
                  <ul>
                    <li>Rank a whole pool by explainable fit %</li>
                    <li>Measured against your job spec — not keywords</li>
                  </ul>
                </div>
                <div className={styles.apifeatGroup}>
                  <div className={styles.apifeatH}>
                    <IconSend size={15} /> Delivery
                  </div>
                  <ul>
                    <li>One API call to assess a candidate</li>
                    <li>Bulk, CSV &amp; shared self-serve links</li>
                  </ul>
                </div>
                <div className={styles.apifeatGroup}>
                  <div className={styles.apifeatH}>
                    <IconShieldLock size={15} /> Commercial
                  </div>
                  <ul>
                    <li>Wholesale pricing · no lock-in</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.contactCard}>
              <form onSubmit={handleContactSubmit}>
                <div className={styles.cform} style={{ marginBottom: 14 }}>
                  <div className={styles.cfield}>
                    <label htmlFor="pf-company">Company</label>
                    <input
                      id="pf-company"
                      required
                      placeholder="Your company"
                      value={contactForm.company}
                      onChange={updateContactField("company")}
                    />
                  </div>
                  <div className={styles.cfield}>
                    <label htmlFor="pf-name">Your name</label>
                    <input
                      id="pf-name"
                      required
                      placeholder="Full name"
                      value={contactForm.name}
                      onChange={updateContactField("name")}
                    />
                  </div>
                  <div className={styles.cfield}>
                    <label htmlFor="pf-email">Work email</label>
                    <input
                      id="pf-email"
                      required
                      type="email"
                      placeholder="you@company.com"
                      value={contactForm.email}
                      onChange={updateContactField("email")}
                    />
                  </div>
                  <div className={styles.cfield}>
                    <label htmlFor="pf-volume">Est. assessments / month</label>
                    <input
                      id="pf-volume"
                      placeholder="e.g. 200–500"
                      value={contactForm.volume}
                      onChange={updateContactField("volume")}
                    />
                  </div>
                </div>
                <div className={styles.cfield} style={{ marginBottom: 16 }}>
                  <label htmlFor="pf-message">Anything else?</label>
                  <textarea
                    id="pf-message"
                    rows={3}
                    placeholder="Roles you hire for, timeline, questions…"
                    value={contactForm.message}
                    onChange={updateContactField("message")}
                  />
                </div>
                <label className={styles.consent}>
                  <input
                    type="checkbox"
                    checked={contactForm.consent}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, consent: e.target.checked }))}
                  />
                  <span>
                    Send me occasional product updates. Leave this unticked and we only use your email to reply to this request.{" "}
                    <Link to="/privacy-policy">Privacy policy</Link>
                  </span>
                </label>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  style={{ width: "100%", justifyContent: "center" }}
                  disabled={submittingContact}
                >
                  {submittingContact ? "Sending…" : "Request a call →"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        <div>
          © {new Date().getFullYear()} Awasource · Psychometric Assessment API for HR firms, agencies & platforms
        </div>
        <div className={styles.footerLinks}>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms-of-service">Terms of Service</Link>
          <span aria-hidden="true">·</span>
          <button type="button" className={styles.cookieLink} onClick={openCookieSettings}>Cookie settings</button>
        </div>
      </footer>
    </div>
  );
};

export default APIPage;
